import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DateObject from "react-date-object";
import gregorian from "react-date-object/calendars/gregorian";
import { CalendarDays, Zap, X, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function DateRangeFilterPro({ onApplyFilter }) {
  const { t } = useTranslation(["user"]);

  const defaultLabel = t("dateRangeFilter.defaultLabel");

  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [displayValue, setDisplayValue] = useState(defaultLabel);

  const popupRef = useRef(null);
  const buttonRef = useRef(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0, width: 420 });

  /* ---------- close on outside click ---------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------- compute popup position ---------- */
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;

    const compute = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const POPUP_WIDTH = Math.min(window.innerWidth * 0.95, 440);
      const GAP = 12;

      let left = rect.right - POPUP_WIDTH;
      if (left < 8) left = 8;
      if (left + POPUP_WIDTH > window.innerWidth - 8) {
        left = window.innerWidth - POPUP_WIDTH - 8;
      }

      setPopupPos({ top: rect.bottom + GAP, left, width: POPUP_WIDTH });
    };

    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open]);

  const formatDate = (d) =>
    d ? `${d.format("YYYY/MM/DD")} (${d.format("HH:mm")})` : "";

  /* ---------- HANDLE APPLY ---------- */
  const handleApply = () => {
    if (!fromDate || !toDate) return;

    const fromTimeNotSelected = fromDate.hour == null && fromDate.minute == null;
    const toTimeNotSelected = toDate.hour == null && toDate.minute == null;

    const fixedFrom = new DateObject(fromDate);
    const fixedTo = new DateObject(toDate);

    if (fromTimeNotSelected) fixedFrom.set("hour", 0).set("minute", 0);
    if (toTimeNotSelected) fixedTo.set("hour", 23).set("minute", 59);

    const fromStr = fixedFrom.format("YYYY/MM/DD HH:mm");
    const toStr = fixedTo.format("YYYY/MM/DD HH:mm");
    setDisplayValue(`${fromStr} ${t("dateRangeFilter.rangeSeparator")} ${toStr}`);
    setOpen(false);

    const startDate = fixedFrom.convert(persian, persian_fa).toDate().toISOString();
    const endDate = fixedTo.convert(persian, persian_fa).toDate().toISOString();

    localStorage.setItem("startDate", startDate);
    localStorage.setItem("endDate", endDate);

    onApplyFilter?.({ startDate, endDate });
  };

  /* ---------- QUICK RANGE ---------- */
  const setQuickRange = (type) => {
    const now = new DateObject({ calendar: persian, locale: persian_fa });
    let baseFrom;

    if (type === "60days") baseFrom = new DateObject({ calendar: persian, locale: persian_fa }).subtract(60, "days");
    else if (type === "30days") baseFrom = new DateObject({ calendar: persian, locale: persian_fa }).subtract(30, "days");
    else if (type === "7days") baseFrom = new DateObject({ calendar: persian, locale: persian_fa }).subtract(7, "days");
    else {
      setFromDate(null);
      setToDate(null);
      setDisplayValue(defaultLabel);
      localStorage.removeItem("startDate");
      localStorage.removeItem("endDate");
      onApplyFilter?.(null);
      return;
    }

    const fromDateObj = new DateObject({
      year: baseFrom.year, month: baseFrom.month, day: baseFrom.day,
      hour: 0, minute: 0, calendar: persian, locale: persian_fa,
    });

    const toDateObj = new DateObject({
      year: now.year, month: now.month, day: now.day,
      hour: now.hour, minute: now.minute, calendar: persian, locale: persian_fa,
    });

    setFromDate(fromDateObj);
    setToDate(toDateObj);
    setDisplayValue(
      `${fromDateObj.format("YYYY/MM/DD HH:mm")} ${t("dateRangeFilter.rangeSeparator")} ${toDateObj.format("YYYY/MM/DD HH:mm")}`
    );
    setOpen(false);

    const startDate = fromDateObj.convert(gregorian).toDate().toISOString();
    const endDate = toDateObj.convert(gregorian).toDate().toISOString();

    localStorage.setItem("startDate", startDate);
    localStorage.setItem("endDate", endDate);
    onApplyFilter?.({ startDate, endDate });
  };

  /* ---------- CLEAR ---------- */
  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    setDisplayValue(defaultLabel);
    localStorage.removeItem("startDate");
    localStorage.removeItem("endDate");
    const now = new DateObject({ calendar: persian, locale: persian_fa });
    const from = new DateObject({ calendar: persian }).subtract(30, "days");

    onApplyFilter?.({
      startDate: from.toDate().toISOString(),
      endDate: now.toDate().toISOString(),
    });
  };

  /* ================================================================
   *  POPUP CONTENT
   * ================================================================ */
  const popupContent = (
    <div
      ref={popupRef}
      style={{
        position: "fixed",
        top: popupPos.top,
        left: popupPos.left,
        width: popupPos.width,
      }}
      className={`
        z-[9999]
        origin-top
        transform transition-all duration-200 ease-out
        ${open ? "opacity-100 scale-100 translate-y-0 visible" : "opacity-0 scale-95 -translate-y-2 invisible pointer-events-none"}
      `}
    >
      {/* ⚠️ این کارت دیگه overflow-hidden نداره تا تقویم DatePicker بتونه بیرون بزنه */}
      <div
        className="
          relative
          rounded-2xl
          bg-white
          dark:!bg-slate-900
          border border-teal-500/20 dark:!border-teal-400/20
          shadow-[0_20px_60px_-20px_rgba(15,118,110,0.55)]
          dark:!shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]
          p-4 sm:p-5
        "
      >
        {/* ⚠️ فقط تزئینات داخل یه wrapper با overflow-hidden → گوشه‌های گرد حفظ می‌شن */}
        <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />
          <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/20 blur-3xl dark:!bg-amber-400/10" />
          <div className="absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-teal-400/20 blur-3xl dark:!bg-teal-500/10" />
        </div>

        <div className="relative flex flex-col gap-4">

          {/* Header */}
          <div className="flex items-center justify-between gap-2 pb-3 border-b border-teal-500/10 dark:!border-teal-400/10">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40">
                <CalendarDays size={14} className="text-amber-400" />
              </span>
              <h4 className="text-sm font-bold text-slate-800 dark:!text-amber-100">
                {t("dateRangeFilter.popupTitle")}
              </h4>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="h-7 w-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors duration-200"
              aria-label={t("dateRangeFilter.close")}
            >
              <X size={16} />
            </button>
          </div>

          {/* Dates + Quick Ranges */}
          <div className="flex flex-col sm:flex-row justify-between items-stretch gap-4">

            <div className="flex-1 min-w-0 flex flex-col gap-3">

              {/* از تاریخ */}
              <div>
                <label className="text-[11px] font-bold text-slate-500 dark:!text-slate-400 mb-1.5 block tracking-wide">
                  {t("dateRangeFilter.fromDate")}
                </label>
                <DatePicker
                  value={fromDate}
                  onChange={(date) => {
                    if (!date) { setFromDate(null); return; }
                    const newDate = new DateObject(date, { calendar: persian, locale: persian_fa });
                    const nowObj = new DateObject({ calendar: persian, locale: persian_fa });

                    if (newDate.format("YYYY/MM/DD") !== nowObj.format("YYYY/MM/DD")) {
                      if (newDate.hour == null && newDate.minute == null) {
                        newDate.set("hour", 0).set("minute", 0);
                      }
                    } else {
                      if (newDate.hour == null && newDate.minute == null) {
                        newDate.set("hour", 0).set("minute", 0);
                      } else if (newDate.hour > nowObj.hour || (newDate.hour === nowObj.hour && newDate.minute > nowObj.minute)) {
                        newDate.set("hour", nowObj.hour).set("minute", nowObj.minute);
                      }
                    }
                    setFromDate(new DateObject(newDate.toDate(), { calendar: persian, locale: persian_fa }));
                  }}
                  calendar={persian}
                  locale={persian_fa}
                  calendarPosition="bottom-right"
                  plugins={[
                    <TimePicker hideSeconds={true} position="right" hideOnSelect={false} input={false} />
                  ]}
                  maxDate={new DateObject({ calendar: persian })}
                  minDate={new DateObject({ calendar: persian }).subtract(3, "month")}
                  closeOnSelect={false}
                  inputClass="
                    w-full px-3 py-2 rounded-lg text-sm
                    text-slate-800 dark:!text-slate-100
                    bg-white/70 dark:!bg-slate-800/60
                    border border-teal-500/20 dark:!border-teal-400/20
                    focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60
                    transition-all duration-200
                  "
                  format="YYYY/MM/DD HH:mm"
                  renderFooter={({ closeCalendar }) => (
                    <button
                      className="mt-2 w-full rounded-lg bg-gradient-to-l from-teal-600 to-teal-500 text-white py-1.5 text-sm font-semibold hover:from-teal-500 hover:to-teal-400 transition-all"
                      onClick={() => closeCalendar()}
                    >
                      {t("dateRangeFilter.confirm")}
                    </button>
                  )}
                />
              </div>

              {/* تا تاریخ */}
              <div>
                <label className="text-[11px] font-bold text-slate-500 dark:!text-slate-400 mb-1.5 block tracking-wide">
                  {t("dateRangeFilter.toDate")}
                </label>
                <DatePicker
                  value={toDate}
                  onChange={(date) => {
                    if (!date) { setToDate(null); return; }
                    const newDate = new DateObject(date, { calendar: persian, locale: persian_fa });
                    const nowObj = new DateObject({ calendar: persian, locale: persian_fa });

                    if (newDate.format("YYYY/MM/DD") === nowObj.format("YYYY/MM/DD")) {
                      if (newDate.hour == null && newDate.minute == null) {
                        newDate.set("hour", nowObj.hour).set("minute", nowObj.minute);
                      } else if (newDate.hour > nowObj.hour || (newDate.hour === nowObj.hour && newDate.minute > nowObj.minute)) {
                        newDate.set("hour", nowObj.hour).set("minute", nowObj.minute);
                      }
                    } else {
                      if (newDate.hour == null && newDate.minute == null) {
                        newDate.set("hour", 23).set("minute", 59);
                      }
                    }

                    if (fromDate && newDate.format("YYYY/MM/DD") === fromDate.format("YYYY/MM/DD")) {
                      const fromMinutes = (fromDate.hour ?? 0) * 60 + (fromDate.minute ?? 0);
                      const toMinutes = (newDate.hour ?? 0) * 60 + (newDate.minute ?? 0);
                      if (toMinutes <= fromMinutes) {
                        newDate.set("hour", fromDate.hour ?? 0).set("minute", (fromDate.minute ?? 0) + 1);
                        if (newDate.minute >= 60) {
                          newDate.set("hour", (newDate.hour + 1) % 24).set("minute", 0);
                        }
                      }
                    }
                    setToDate(new DateObject(newDate.toDate(), { calendar: persian, locale: persian_fa }));
                  }}
                  calendar={persian}
                  locale={persian_fa}
                  calendarPosition="bottom-right"
                  plugins={[
                    <TimePicker hideSeconds position="right" hideOnSelect={false} input={false} />
                  ]}
                  maxDate={new DateObject({ calendar: persian })}
                  minDate={fromDate ? new DateObject(fromDate.toDate(), { calendar: persian, locale: persian_fa }) : new DateObject({ calendar: persian }).subtract(1, "month")}
                  closeOnSelect={false}
                  inputClass="
                    w-full px-3 py-2 rounded-lg text-sm
                    text-slate-800 dark:!text-slate-100
                    bg-white/70 dark:!bg-slate-800/60
                    border border-teal-500/20 dark:!border-teal-400/20
                    focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60
                    transition-all duration-200
                  "
                  format="YYYY/MM/DD HH:mm"
                  renderFooter={({ closeCalendar }) => (
                    <button
                      className="mt-2 w-full rounded-lg bg-gradient-to-l from-teal-600 to-teal-500 text-white py-1.5 text-sm font-semibold hover:from-teal-500 hover:to-teal-400 transition-all"
                      onClick={() => closeCalendar()}
                    >
                      {t("dateRangeFilter.confirm")}
                    </button>
                  )}
                />
              </div>
            </div>

            {/* Quick Ranges */}
            <div className="flex flex-row sm:flex-col justify-center gap-2 shrink-0">
              {[
                { key: "7days" },
                { key: "30days" },
                { key: "60days" },
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => setQuickRange(item.key)}
                  className="
                    group/quick relative overflow-hidden
                    flex items-center justify-center gap-1.5
                    text-[11px] font-bold
                    px-3 py-2 rounded-lg
                    text-amber-900 dark:!text-amber-100
                    bg-gradient-to-l from-amber-300/70 to-amber-400/40
                    dark:!from-amber-400/20 dark:!to-amber-300/10
                    border border-amber-300/50 dark:!border-amber-300/30
                    backdrop-blur-md
                    transition-all duration-300
                    hover:from-amber-300 hover:to-amber-400
                    hover:shadow-[0_4px_16px_-4px_rgba(251,191,36,0.7)]
                    hover:scale-[1.03] active:scale-95
                    whitespace-nowrap
                  "
                >
                  <Zap size={12} className="shrink-0" />
                  {t(`dateRangeFilter.quickRanges.${item.key}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-3 border-t border-teal-500/10 dark:!border-teal-400/10">
            <button
              onClick={handleApply}
              disabled={!fromDate || !toDate}
              className={`
                flex-1 flex items-center justify-center gap-1.5
                py-2.5 px-3 rounded-xl text-sm font-bold
                transition-all duration-300
                ${fromDate && toDate
                  ? "bg-gradient-to-l from-teal-700 to-teal-500 text-white shadow-[0_6px_20px_-6px_rgba(15,118,110,0.7)] hover:from-teal-600 hover:to-teal-400 hover:shadow-[0_8px_24px_-6px_rgba(20,184,166,0.85)] active:scale-[0.98]"
                  : "bg-slate-200/60 dark:!bg-slate-800/60 text-slate-400 dark:!text-slate-600 cursor-not-allowed"
                }
              `}
            >
              <Check size={15} />
              {t("dateRangeFilter.apply")}
            </button>

            <button
              onClick={handleClear}
              className="
                flex items-center justify-center gap-1.5
                px-3 py-2.5 rounded-xl text-xs font-bold
                text-rose-600 dark:!text-rose-300
                border border-rose-400/40 dark:!border-rose-400/30
                bg-rose-500/5 hover:bg-rose-500/15
                transition-all duration-300
                hover:shadow-[0_4px_16px_-6px_rgba(244,63,94,0.6)]
                active:scale-95
              "
            >
              <X size={14} />
              {t("dateRangeFilter.clear")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative inline-block text-right w-full sm:w-auto">
      {/* Trigger */}
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="
          group w-full sm:w-auto
          flex items-center justify-between sm:justify-start gap-3
          px-3 sm:px-4 py-2.5 rounded-xl
          text-teal-700 dark:!text-amber-100
          bg-white/60 dark:!bg-white/5
          border border-teal-500/20 dark:!border-teal-400/20
          backdrop-blur-md
          transition-all duration-300
          hover:border-amber-300/60
          hover:bg-amber-300/10
          hover:shadow-[0_0_20px_-6px_rgba(251,191,36,0.45)]
          active:scale-[0.98]
        "
      >
        <span
          className="
            shrink-0 flex h-9 w-9 items-center justify-center rounded-lg
            bg-gradient-to-br from-amber-300/30 to-teal-400/20
            border border-amber-300/40
            text-amber-400
            transition-transform duration-500
            group-hover:scale-105 group-hover:rotate-3
          "
        >
          <CalendarDays size={17} />
        </span>
        <span
          className={`
            flex-1 sm:flex-initial text-[13px] sm:text-sm font-semibold truncate
            ${displayValue === defaultLabel ? "text-slate-500 dark:!text-slate-400" : "text-slate-800 dark:!text-amber-100"}
          `}
        >
          {displayValue}
        </span>
      </button>

      {/* Portal */}
      {typeof document !== "undefined" &&
        createPortal(popupContent, document.body)}
    </div>
  );
}